/* LBRGT Versprechen - pagination for the mobile person slider (dots plus the prev/next
   circles from Figma 252:3035), plus the keyboard affordance for both horizontal strips.
   Progressive enhancement: without this file the track stays an ordinary scrollable strip,
   so there are never dead buttons. The dots only exist when the track really overflows,
   which is why they are built here and not in Liquid - whether it overflows depends on the
   viewport, not on the number of blocks.
   tabindex is handed out here for the same reason: a scroll container has to be reachable by
   keyboard (WCAG 2.1.1), but on desktop neither strip scrolls, and a focus stop that does
   nothing is noise. */
(function () {
  'use strict';

  /* theme-editor.js re-runs every section script on shopify:section:load, so this file can
     be evaluated more than once per page. A second copy would install a second set of
     document listeners and - with a fresh module scope - could no longer reach the
     observers the first copy created. The first copy stays alive and keeps re-syncing, so
     later copies simply stand down. */
  if (window.lbrgtVersprechenLoaded) return;
  window.lbrgtVersprechenLoaded = true;

  var MOBILE_QUERY = '(max-width: 768.98px)';
  var ACTIVE_RATIO = 0.6;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1px tolerance: scrollWidth and clientWidth are rounded, so an exactly fitting strip can
     report a phantom overflow. */
  function overflows(element) {
    return element.scrollWidth > element.clientWidth + 1;
  }

  /* A strip whose client box is 0 wide has not been laid out: an ancestor is display:none,
     the section is mid-reload in the theme editor, or the document is not being rendered
     at all (a hidden preview pane reports a 0 viewport). overflows() then compares 0 with
     0, reads "fits, no pagination needed" and the dots get cleared. That is how this
     section lost its pagination twice - both times only in a measuring setup, never on a
     device. An unmeasurable strip is therefore skipped, not answered: whatever it already
     has stays until a real measurement comes in. */
  function measurable(element) {
    return element.clientWidth > 0;
  }

  /* The observer lives on the element, not in a module level WeakMap: that way any copy of
     this script can find and disconnect it instead of leaking the previous one. */
  function teardown(root) {
    if (root.lbrgtVspObserver) {
      root.lbrgtVspObserver.disconnect();
      root.lbrgtVspObserver = null;
    }
    var dots = root.querySelector('[data-vsp-dots]');
    if (dots) {
      dots.textContent = '';
      dots.removeAttribute('role');
      dots.removeAttribute('aria-label');
    }
    delete root.dataset.vspReady;
  }

  /* Overflowing is not the same as scrollable. The trust strip overflows its band on
     purpose while the marquee runs - the content is deliberately several viewports wide -
     but the band clips it and nothing can be scrolled. Asking the computed overflow instead
     of guessing from the class keeps this correct in every state the CSS can produce,
     including prefers-reduced-motion, which Liquid never sees. */
  function scrollable(element) {
    var overflow = window.getComputedStyle(element).overflowX;
    return overflow === 'auto' || overflow === 'scroll';
  }

  /* Only a strip that actually scrolls becomes a tab stop. */
  function syncFocusability(root) {
    var strips = root.querySelectorAll('[data-vsp-scroll]');
    Array.prototype.forEach.call(strips, function (strip) {
      if (!measurable(strip)) return;
      if (scrollable(strip) && overflows(strip)) strip.setAttribute('tabindex', '0');
      else strip.removeAttribute('tabindex');
    });
  }

  function buildDots(root) {
    var track = root.querySelector('[data-vsp-track]');
    var dots = root.querySelector('[data-vsp-dots]');
    if (!track || !dots) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('.lbrgt-vsp__card'));
    // a single card never scrolls - that is a fact about the markup, not about the layout
    if (cards.length < 2) {
      teardown(root);
      return;
    }
    // no verdict without a laid out track, see measurable()
    if (!measurable(track)) return;
    // on desktop the list is a static column and the dots do not belong there
    if (!overflows(track)) {
      teardown(root);
      return;
    }
    if (root.dataset.vspReady === 'true') return;

    function scrollToCard(index) {
      var card = cards[index];
      if (!card) return;
      card.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }

    /* Figma 252:3035 - the prev/next pair. In the file that frame is detached and parked
       over the FOLLOWING frame, which is exactly how it was reported ("the slider buttons
       slipped down onto Versand"). It belongs to this row: the frame is 400.76 wide, which
       is this section's mobile content box (430 - 2 x 15) to the pixel, and its 321px inner
       gap puts one circle flush against each gutter with the dots centred between them. */
    function navButton(name, label) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lbrgt-vsp__nav lbrgt-vsp__nav--' + name;
      button.setAttribute('aria-label', label);
      var icon = document.createElement('span');
      icon.className = 'lbrgt-vsp__nav-icon';
      icon.setAttribute('aria-hidden', 'true');
      button.appendChild(icon);
      return button;
    }

    var prev = navButton('prev', 'Vorherige Person');
    var next = navButton('next', 'Nächste Person');

    /* The dots need their own box: the row is a space-between of three parts, and only
       equal-width end caps keep the dot group optically centred the way the mock has it. */
    var group = document.createElement('span');
    group.className = 'lbrgt-vsp__dot-group';

    var buttons = cards.map(function (card, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'lbrgt-vsp__dot';
      button.setAttribute('aria-label', 'Person ' + (index + 1) + ' anzeigen');
      button.addEventListener('click', function () {
        scrollToCard(index);
      });
      group.appendChild(button);
      return button;
    });

    dots.appendChild(prev);
    dots.appendChild(group);
    dots.appendChild(next);

    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Personen durchblättern');

    var current = 0;

    function setActive(active) {
      current = active;
      buttons.forEach(function (button, index) {
        if (index === active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      /* aria-disabled, not the disabled attribute: at either end of the track the button
         has nothing left to do, but a disabled button is removed from the tab order and
         the browser blurs it, so paging to the last card with the keyboard would drop
         focus to the body mid-interaction. The click is guarded below instead. */
      prev.setAttribute('aria-disabled', active <= 0 ? 'true' : 'false');
      next.setAttribute('aria-disabled', active >= cards.length - 1 ? 'true' : 'false');
    }

    prev.addEventListener('click', function () {
      if (current > 0) scrollToCard(current - 1);
    });
    next.addEventListener('click', function () {
      if (current < cards.length - 1) scrollToCard(current + 1);
    });

    /* The mock always shows exactly one wide dot, so the strip must never sit in a state
       where none is marked. The observer below cannot guarantee that on its own: it only
       delivers while the document is really being rendered, so a page restored from
       bfcache, a tab opened in the background or a prerender can show three equal dots
       until the first scroll. Seed the state from the geometry instead - the card whose
       centre is closest to the track centre - and let the observer take over from there. */
    function activeFromScroll() {
      var trackBox = track.getBoundingClientRect();
      var centre = trackBox.left + trackBox.width / 2;
      var best = 0;
      var bestDistance = Infinity;
      cards.forEach(function (card, index) {
        var box = card.getBoundingClientRect();
        var distance = Math.abs(box.left + box.width / 2 - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    setActive(activeFromScroll());

    /* threshold only decides WHEN the callback runs, never what it reports: isIntersecting
       is already true at any overlap above zero, so a batch could end on the card that is
       leaving the viewport. The ratio has to be re-checked per entry. */
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < ACTIVE_RATIO) return;
          var active = cards.indexOf(entry.target);
          if (active < 0) return;
          setActive(active);
        });
      },
      { root: track, threshold: ACTIVE_RATIO }
    );

    cards.forEach(function (card) {
      observer.observe(card);
    });

    root.lbrgtVspObserver = observer;
    root.dataset.vspReady = 'true';
  }

  /* Whether either strip scrolls is a question about the LAID OUT section, so it cannot be
     answered before this section's stylesheet has applied. From the third section down the
     page the Liquid loads that stylesheet with media="print" + onload, which means on a
     cold cache it lands AFTER DOMContentLoaded: overflows() then measured an unstyled
     block, found no overflow, tore the dots down and nothing ever asked again - three
     cards and no pagination at all. A ResizeObserver on the track closes that window: it
     delivers once on observe and again on every later size change, so late CSS, a swapped
     web font and a rotated phone all re-run the same check. */
  function watchTrack(root) {
    if (!window.ResizeObserver || root.lbrgtVspResize) return;
    var track = root.querySelector('[data-vsp-track]');
    if (!track) return;
    root.lbrgtVspResize = new ResizeObserver(function () {
      syncFocusability(root);
      buildDots(root);
    });
    root.lbrgtVspResize.observe(track);
  }

  function apply(root) {
    watchTrack(root);
    syncFocusability(root);
    buildDots(root);
  }

  function sync(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = context.querySelectorAll('.lbrgt-vsp');
    Array.prototype.forEach.call(roots, apply);
    // a section:load event hands us the section wrapper itself, not an ancestor
    if (context !== document && context.classList && context.classList.contains('lbrgt-vsp')) {
      apply(context);
    }
  }

  function init() {
    sync(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The fallback for engines without ResizeObserver, where watchTrack() stands down and
     the DOMContentLoaded pass is the only measurement - too early when this file's
     stylesheet arrives via media="print" + onload from the third section down. load fires
     after every stylesheet has applied, so the strip is styled by then.
     It is NOT a cure for a document that is not being painted. That idea was behind an
     earlier round of this file; measured at 430px in a real headless Chrome, the dots build
     from the ResizeObserver alone, and in an unpainted document this pass measures the same
     void as every other one. measurable() is what keeps that void from clearing the dots.
     buildDots returns early once data-vsp-ready is set, so on the normal path this costs
     one measurement and nothing else. */
  if (document.readyState !== 'complete') {
    window.addEventListener('load', init);
  }

  // the viewport decides whether either strip scrolls at all
  var mobile = window.matchMedia(MOBILE_QUERY);
  var onViewportChange = function () {
    sync(document);
  };
  if (mobile.addEventListener) mobile.addEventListener('change', onViewportChange);
  else if (mobile.addListener) mobile.addListener(onViewportChange);

  // theme editor: adding or picking a block re-renders the section
  document.addEventListener('shopify:section:load', function (event) {
    sync(event.target);
  });
  document.addEventListener('shopify:block:select', function () {
    sync(document);
  });
})();

// With reduced motion the marquee stands still and its row becomes the scrollable tab stop, so
// the container's own stop ("Fokus hält es an") would do nothing (retest 22.10.)
(function () {
  if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('.lbrgt-vsp__trust--marquee[tabindex]').forEach(function (el) {
    el.removeAttribute('tabindex');
    el.removeAttribute('role');
    el.removeAttribute('aria-label');
  });
})();
